import React, { useState } from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface Service {
  id: number;
  title: string;
  icon: string;
  description: string;
  benefits: string[];
}

const Services: React.FC = () => {
  const { elementRef, isVisible } = useScrollAnimation({ threshold: 0.2, triggerOnce: false });
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  const services: Service[] = [
    {
      id: 1,
      title: "Harmonização Facial",
      icon: "M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
      description: "Procedimentos integrados que equilibram proporções faciais com técnicas minimamente invasivas.",
      benefits: ["Resultado natural", "Técnica avançada", "Efeito imediato"]
    },
    {
      id: 2,
      title: "Transplante Capilar",
      icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z",
      description: "Implante capilar com técnica FUE moderna. Cabelo natural, resultado permanente e cicatrização imperceptível.",
      benefits: ["Técnica FUE", "Resultado permanente", "Recuperação rápida"]
    },
    {
      id: 3,
      title: "Bioestimulador de Colágeno",
      icon: "M13 10V3L4 14h7v7l9-11h-7z",
      description: "Tratamento revolucionário que estimula a produção natural de colágeno, promovendo rejuvenescimento profundo e duradouro.",
      benefits: ["Efeito prolongado", "Rejuvenescimento natural", "Estimula colágeno"]
    },
    {
      id: 4,
      title: "Rinomodelação",
      icon: "M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z",
      description: "Corrige imperfeições do nariz sem cirurgia. Técnica precisa, resultado imediato.",
      benefits: ["Sem cirurgia", "Resultado imediato", "Procedimento rápido"]
    },
    {
      id: 5,
      title: "Cirurgias da face",
      icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
      description: "Procedimentos cirúrgicos de excelência para rejuvenescimento facial completo, com resultados naturais e duradouros.",
      benefits: [
        "Blefaroplastia",
        "Lift de sobrancelha",
        "Facelift",
        "Liplift",
        "Bichectomia"
      ]
    },
    {
      id: 6,
      title: "Harmonização Corporal",
      icon: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z",
      description: "Modelagem corporal com técnicas avançadas para contorno e volume natural dos glúteos. Inclui opção com Bioestimulador de Colágeno nos glúteos para melhorar firmeza e textura.",
      benefits: ["Volume natural", "Contorno definido", "Resultado harmônico", "Bioestimulador de Colágeno nos glúteos"]
    }
    ,
    {
      id: 7,
      title: "Peeling",
      icon: "M12 2a10 10 0 100 20 10 10 0 000-20z",
      description: "Peelings químicos e físicos para renovação da pele, melhora de textura, manchas e poros dilatados.",
      benefits: ["Renovação da pele", "Redução de manchas", "Melhora de textura e poros"]
    },
    {
      id: 8,
      title: "Preenchimentos (Full Face)",
      icon: "M16 7a4 4 0 11-8 0 4 4 0 018 0z",
      description: "Preenchimentos para harmonização facial: Full Face, Bigode Chinês, Mandíbula, Mento e definição de contornos para resultado natural e duradouro.",
      benefits: ["Harmonização global", "Restituição de volume", "Contorno facial definido"]
    },
    {
      id: 9,
      title: "Tratamento de Microvasos",
      icon: "M3 8a9 9 0 0118 0v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z",
      description: "Técnicas minimamente invasivas para remoção e redução de microvasos e vasinhos faciais e corporais, melhorando a aparência da pele.",
      benefits: ["Redução de vasinhos", "Procedimento rápido", "Recuperação rápida"]
    }
  ];

  const toggleCard = (id: number) => {
    setExpandedCard(expandedCard === id ? null : id);
  };

  return (
    <section ref={elementRef as React.RefObject<HTMLElement>} className="relative py-20 md:py-28 bg-gradient-to-b from-white to-gray-50 overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-[10%] w-64 h-64 bg-secondary/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 left-[10%] w-64 h-64 bg-primary/5 rounded-full blur-3xl"></div>

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-block px-6 py-2 bg-secondary/10 text-secondary font-semibold text-sm tracking-wider rounded-full mb-6">
            NOSSOS SERVIÇOS
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 mb-6">
            Excelência em Estética
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            Oferecemos uma variedade de tratamentos estéticos de alta qualidade para realçar sua beleza natural com resultados harmoniosos.
          </p>
        </div>

        {/* Divider */}
        <div className="w-24 h-1 bg-gradient-to-r from-secondary to-primary mx-auto mb-16"></div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`group relative transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div
                className={`relative bg-white rounded-2xl overflow-hidden transition-all duration-500 cursor-pointer border-2 ${
                  expandedCard === service.id 
                    ? 'border-secondary shadow-2xl' 
                    : 'border-gray-100 hover:border-secondary/30 shadow-lg hover:shadow-xl'
                }`}
                onClick={() => toggleCard(service.id)}
              >
                {/* Card Header */}
                <div className="p-8">
                  {/* Icon */}
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 transition-all duration-500 ${
                    expandedCard === service.id
                      ? 'bg-gradient-to-br from-secondary to-primary'
                      : 'bg-secondary/10 group-hover:bg-secondary/20'
                  }`}>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className={`h-8 w-8 transition-colors duration-500 ${
                        expandedCard === service.id ? 'text-white' : 'text-secondary'
                      }`}
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={service.icon} />
                    </svg>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl md:text-2xl font-serif font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors duration-300">
                    {service.title}
                  </h3>

                  {/* Expand/Collapse indicator */}
                  <div className="flex items-center gap-2 text-secondary text-sm font-semibold">
                    <span>{expandedCard === service.id ? 'Ver menos' : 'Saiba mais'}</span>
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      className={`h-4 w-4 transition-transform duration-300 ${expandedCard === service.id ? 'rotate-180' : ''}`}
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Expandable Content */}
                <div
                  className={`overflow-hidden transition-all duration-500 ${
                    expandedCard === service.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-8 pb-8 border-t border-gray-100 pt-6">
                    {/* Description */}
                    <p className="text-gray-600 leading-relaxed mb-6">
                      {service.description}
                    </p>

                    {/* Benefits */}
                    <div className="space-y-2 mb-6">
                      {service.benefits.map((benefit, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <svg className="w-5 h-5 text-secondary flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <a
                      href="/agendar"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-secondary to-primary text-white font-semibold text-sm rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105 w-full justify-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Agendar Avaliação
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`text-center mt-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="text-gray-600 mb-6">
            Não encontrou o procedimento que procura?
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white border-2 border-secondary text-secondary font-semibold text-sm rounded-xl hover:bg-secondary hover:text-white transition-all duration-300 hover:scale-105"
          >
            Fale com Especialista
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Services;
